import React from "react";
import PropTypes from "prop-types";
import SecurityQuestionsComponent from "./labeledinput/SecurityQuestionsComponent";
import {isFormSubmittable, setFormState} from "../../util/FormUtil";
import UserDataService from "../common/UserDataService";
import PhoneVerificationComponent from "./PhoneVerificationComponent";
import AlertComponent from "../../util/AlertComponent";
import ProcessingComponent from "../../util/ProcessingComponent";
import {extractMessage, fetchResponseHandler} from "../../util/FetchUtil";
import DuoSetupComponent from "./DuoSetupComponent";
import DuoAuthSetupComponent from "./DuoAuthSetupComponent";
import WorkInfoComponent from "./labeledinput/WorkInfoComponent";

const PAGE_FORM = "form";
const PAGE_PHONE_VERIFY = "phoneVerify";
const PAGE_DUO_SETUP = "duoSetup";

class EnhancedSecurityComponent extends React.Component {
    constructor(props){
        super(props);
        const {seedData} = props;
        this.state = {page: PAGE_FORM, data: seedData ? seedData : {}, metadata: {}};

        this.removeMessage = this.removeMessage.bind(this);
        this.setMessage = this.setMessage.bind(this);
        this.setPage = this.setPage.bind(this);
        this.setFailToRetrievePhoneFromDuo = this.setFailToRetrievePhoneFromDuo.bind(this);
        this.showFormPage = this.showFormPage.bind(this);
        this.showPhoneVerificationPage = this.showPhoneVerificationPage.bind(this);
        this.setFormState = this.setFormState.bind(this);
        this.updateUserData = this.updateUserData.bind(this);
        this.setShowProcessing = this.setShowProcessing.bind(this);
        this.setDuoSetupErrorMessage = this.setDuoSetupErrorMessage.bind(this);
        this.duoSetupSuccess = this.duoSetupSuccess.bind(this);
        this.duoSetupCancel = this.duoSetupCancel.bind(this);
        this.setExtractSecQnA = this.setExtractSecQnA.bind(this);
        this.setExtractWorkInfo =  this.setExtractWorkInfo.bind(this);
    }

    removeMessage(){
        this.setMessage(null);
    }

    setMessage(e, messageType = "error"){
        let alertMessage = extractMessage(e);
        this.setState(state => ({...state, alertMessage, alertMessageType: messageType}));
    }

    setPage(page){
        this.setState(state => ({...state, page}));
    }

    setFailToRetrievePhoneFromDuo (failToRetrievePhoneFromDuo) {
        this.setState(state => ({...state, failToRetrievePhoneFromDuo}));
    }

    showPhoneVerificationPage(){
        this.setPage(PAGE_PHONE_VERIFY);
    }

    showFormPage(){
        this.setPage(PAGE_FORM);
    }

    setFormState(...args){
        // we are taking updater function in setState so that successive setState calls won't overwrite each other.
        this.setState(state => setFormState(state, args));
    }

    setExtractSecQnA(func){
        this.extractSecQnA = func;
    }

    setExtractWorkInfo(func){
        this.extractWorkInfo = func;
    }

    updateUserData(phoneVerificationReceipt){
        const {contextId, userDataService, onSuccess, setUpMfa} = this.props;
        this.setShowProcessing(true);

        userDataService.toPremiumCgl({
            requestId: contextId,
            phoneVerificationReceipt,
            securityQuestionsInfo: this.extractSecQnA(),
            workInfo: this.extractWorkInfo()
        })
            .then(fetchResponseHandler)
            .then(data => {
                if (data.updateReceipt === 'DUO_PHONE_IMPORT_ERROR') {
                    // AREAAA-793: Backend can't retrieve the user's phone number from DUO. Ask user to enter it manually
                    this.setMessage("Error occurred when finding the phone number in your account. Please enter phone number below.")
                    this.setFailToRetrievePhoneFromDuo(true);
                } else {
                    if (setUpMfa) {
                        this.setPage(PAGE_DUO_SETUP);
                        this.setState(state => ({...state, accountUpgradeData: data}));
                    } else {
                        onSuccess(data);
                    }
                }
            })
            .catch(e => this.setMessage(e))
            .finally(() => this.setShowProcessing(false));
    }

    setShowProcessing(showProcessing){
        this.setState(state => ({...state, showProcessing}));
    }

    setDuoSetupErrorMessage(e){
        const message =
            "Your account security is now enhanced, however your Duo setup failed. You can try setting up your account the next time you login. " +
            extractMessage(e);
        this.setMessage(message);
    }

    duoSetupSuccess(){
        const {onSuccess} = this.props;
        const {accountUpgradeData} = this.state;
        onSuccess({...accountUpgradeData, duoSetupSuccess: true});
    }

    duoSetupCancel(e){
        if (e){
            e.preventDefault();
        }
        const {onSuccess} = this.props;
        const {accountUpgradeData} = this.state;
        onSuccess(accountUpgradeData);
    }

    render() {
        const {userDataInit, onCancel, userDataService, customIntro, allowSkipMfaSetUp, allowSkipDuoSetUp=true, allowSkipPhoneInput,skipIpStackServiceCall,enableUniversalDuo} = this.props;
        const {page, data, metadata, showProcessing, alertMessage, alertMessageType, failToRetrievePhoneFromDuo=false} = this.state;
        const submitDisabled = ! isFormSubmittable(metadata);
        const hidePhoneInput = allowSkipPhoneInput && !failToRetrievePhoneFromDuo;
        return (
            <div>
                <AlertComponent message={alertMessage} type={alertMessageType} onClose={this.removeMessage} />
                <ProcessingComponent show={showProcessing} />
                <h1>Add multi-factor authentication to your account</h1>
                {
                    page === PAGE_FORM && (customIntro ? customIntro : <div>This will take 5-7 minutes.</div>)
                }
                {
                    page === PAGE_FORM &&
                    <div>
                        <p className="weight--light">
                            All fields are required unless noted as optional
                        </p>
                        <WorkInfoComponent
                            data={data}
                            metadata={metadata}
                            setFormState={this.setFormState}
                            userDataInit={userDataInit}
                            userDataService={userDataService}
                            onExtractData={this.setExtractWorkInfo}
                            countryCodeWidth="20"
                            onError={this.setMessage}
                            allowSkipPhoneInput={hidePhoneInput}
                            skipIpStackServiceCall={skipIpStackServiceCall}
                        />
                        <SecurityQuestionsComponent
                            data={data}
                            metadata={metadata}
                            setFormState={this.setFormState}
                            userDataInit={userDataInit}
                            required={true}
                            onExtractData={this.setExtractSecQnA}
                        />
                        <div className="text--center">
                            <button className="btn btn--link" onClick={onCancel}>Cancel</button>
                            <button disabled={submitDisabled}
                                    onClick={hidePhoneInput ? () => this.updateUserData("PVR-TBD") : this.showPhoneVerificationPage}
                                    className="btn btn--large push-half--left">Continue
                            </button>
                        </div>
                    </div>
                }
                {
                    page === PAGE_PHONE_VERIFY &&
                    <PhoneVerificationComponent
                        countryCode={data.phoneCountryCode}
                        phoneNumber={data.phoneNumber}
                        extension={data.phoneExtension}
                        channel={data.phone2FAChannel}
                        userDataService={userDataService}
                        onCancel={this.showFormPage}
                        onError={this.setMessage}
                        onSuccess={this.updateUserData}
                        securityQnAData={this.extractSecQnA()}
                    />
                }
                {
                    page === PAGE_DUO_SETUP &&
                        <div className="text--center">
                            {enableUniversalDuo ?
                                <DuoAuthSetupComponent
                                    userDataService={userDataService}
                                    onError={this.setDuoSetupErrorMessage}
                                    onSuccess={this.duoSetupSuccess}
                                />
                                :
                                <DuoSetupComponent
                                    userDataService={userDataService}
                                    onError={this.setDuoSetupErrorMessage}
                                    onSuccess={this.duoSetupSuccess}
                                />
                            }
                            {   // allowSkipDuoSetUp is derived based on targetDescription existing in reloadable prop duooconversion.noSkipApps
                                (allowSkipMfaSetUp && allowSkipDuoSetUp) &&
                                <div className="text--center push--top">
                                    <a href="#" onClick={this.duoSetupCancel}>I will set it up later</a>
                                </div>
                            }
                        </div>
                }
            </div>
        );
    }
}

EnhancedSecurityComponent.propTypes = {
    userDataInit: PropTypes.object.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    userDataService: PropTypes.instanceOf(UserDataService).isRequired,
    contextId: PropTypes.string.isRequired,
    setUpMfa: PropTypes.bool,
    // this property will only take effect if setUpMfa is set to true
    allowSkipMfaSetUp: PropTypes.bool,
    seedData: PropTypes.object,
    customIntro: PropTypes.object,
    allowSkipPhoneInput: PropTypes.bool,
    skipIpStackServiceCall: PropTypes.bool,
    enableUniversalDuo: PropTypes.bool
};
export default EnhancedSecurityComponent;